import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import { useCrmOpportunities, useUpdateOpportunity } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

// Stage config
const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: '🆕' },
  active: { label: 'Active', color: 'bg-orange-100 text-orange-700', icon: '💬' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700', icon: '✅' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: '⏸️' },
};

export default function CrmOpportunitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts, showToast, hideToast } = useToast();
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [stage, setStage] = useState(searchParams.get('stage') || 'all');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useCrmOpportunities(page, 20, stage === 'all' ? null : stage, null, search || null);
  const updateMutation = useUpdateOpportunity();
  
  const opportunities = data?.opportunities || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1 };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (stage && stage !== 'all') params.set('stage', stage);
    setSearchParams(params);
  };

  const handleStageChange = (opportunityId, newStage) => {
    updateMutation.mutate(
      { opportunityId, stage: newStage },
      {
        onSuccess: () => {
          showToast('Stage updated', 'success');
        },
        onError: (error) => {
          showToast(error.message || 'Failed to update stage', 'error');
        },
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `£${parseFloat(value).toLocaleString()}`;
  };

  const getContactName = (opp) => {
    const parts = [opp.contactFirstName, opp.contactLastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Search & Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search opportunities..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={stage}
              onChange={(e) => {
                setStage(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            >
              <option value="all">All Stages</option>
              <option value="open">Open (New + Active)</option>
              <option value="new">New</option>
              <option value="active">Active</option>
              <option value="converted">Converted</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-gray-900">Opportunities</h2>
          <div className="flex gap-3">
            <Link
              to="/crm/opportunities/new"
              className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium text-sm"
            >
              + Add Opportunity
            </Link>
            <Link
              to="/crm/contacts"
              className="inline-flex items-center px-4 py-2 text-zapt-purple border border-zapt-purple rounded-lg hover:bg-zapt-purple/5 font-medium text-sm"
            >
              View Contacts
            </Link>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Potential deals or projects linked to contacts. Track estimated and actual values, and move through stages from New to Converted.
        </p>
      </div>

      {/* Opportunities Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex gap-4">
                  <div className="h-10 bg-gray-200 rounded w-10"></div>
                  <div className="flex-1 h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {search || stage !== 'all' ? 'No opportunities match your filters' : 'No opportunities yet'}
            </p>
            <Link
              to="/crm/opportunities/new"
              className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
            >
              Add your first opportunity
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Opportunity</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Contact</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Stage</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Est. Initial</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Est. Monthly</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Next Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {opportunities.map((opp) => {
                  const stageConfig = STAGE_CONFIG[opp.stage] || STAGE_CONFIG.new;
                  return (
                    <tr key={opp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/crm/opportunities/${opp.id}`}
                          className="font-medium text-gray-900 hover:text-zapt-purple"
                        >
                          {opp.title}
                        </Link>
                        {opp.linkedAppName && (
                          <span className="text-xs text-gray-500 block">App: {opp.linkedAppName}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/crm/contacts/${opp.contactId}`}
                          className="text-sm text-zapt-purple hover:underline"
                        >
                          {getContactName(opp)}
                        </Link>
                        {opp.contactCompany && (
                          <span className="text-xs text-gray-500 block">{opp.contactCompany}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={opp.stage}
                          onChange={(e) => handleStageChange(opp.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-lg border-0 ${stageConfig.color}`}
                        >
                          {Object.entries(STAGE_CONFIG).map(([value, config]) => (
                            <option key={value} value={value}>{config.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatCurrency(opp.estimatedInitialValue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatCurrency(opp.estimatedMonthlyValue)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {opp.nextAction ? (
                          <div>
                            <span className="text-gray-900 block truncate max-w-[200px]">{opp.nextAction}</span>
                            {opp.nextActionDue && (
                              <span className={`text-xs ${new Date(opp.nextActionDue) < new Date() ? 'text-red-600' : 'text-gray-500'}`}>
                                Due: {formatDate(opp.nextActionDue)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} opportunities)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}
